import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * HaveVanish — "The Missing 'Have'" hook for the ʿindī / lī lesson.
 *
 * Each row shows an English sentence with its have/has highlighted; tapping
 * it strikes the verb out and reveals the literal "at-me" reading plus the
 * verbless Arabic sentence — owner ending highlighted — with audio.
 * Completes when every have has been tapped away.
 */
export default function HaveVanish({ data, onComplete, audio }) {
  const [vanishedIds, setVanishedIds] = useState(() => new Set());

  const items = data.items;
  const allVanished = vanishedIds.size >= items.length;

  useEffect(() => {
    if (allVanished) onComplete();
  }, [allVanished, onComplete]);

  const fullArabic = (item) => `${item.have_ar} ${item.rest_ar}`;

  const handleVanish = (item) => {
    if (vanishedIds.has(item.id)) return;
    setVanishedIds((prev) => new Set(prev).add(item.id));
    if (audio) audio.play(fullArabic(item));
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{vanishedIds.size}/{items.length} vanished</ProgressPill>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const vanished = vanishedIds.has(item.id);
          const suffixBare = item.suffix.replace(/^ـ/, "");
          const stem = item.have_ar.slice(0, item.have_ar.length - suffixBare.length);
          return (
            <div
              key={item.id}
              className="rounded-xl px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <p className="text-lg m-0" style={{ color: "var(--parchment)" }}>
                {item.en_before}{" "}
                {vanished ? (
                  <span className="line-through opacity-40" style={{ color: "var(--parchment-dim)" }}>
                    {item.have_word}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleVanish(item)}
                    className="rounded-md px-1.5 transition-all hover:brightness-125 animate-pulse font-semibold"
                    style={{
                      color: "var(--gold-bright)",
                      background: "var(--gold-bg)",
                      border: "1px dashed var(--gold-dim)",
                      fontSize: "inherit",
                      lineHeight: "inherit",
                    }}
                    title="Tap to see what Arabic does with this word"
                  >
                    {item.have_word}
                  </button>
                )}{" "}
                {item.en_after}
              </p>

              {vanished && (
                <div className="m-0 flex flex-col items-start sm:items-end gap-0.5">
                  <p className="m-0 flex items-center gap-3">
                    <span dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7]" style={{ color: "var(--parchment)" }}>
                      {stem}
                      <span style={{ color: "var(--tag-violet)" }}>{suffixBare}</span>{" "}
                      {item.rest_ar}
                    </span>
                    {audio && (
                      <PlayButton text={fullArabic(item)} playingText={audio.playingText} onPlay={audio.play} />
                    )}
                  </p>
                  <p className="text-xs m-0 italic" style={{ color: "var(--gold-dim)" }}>
                    literally: {item.literal_en}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allVanished && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">✨</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
